import React, { useId, useRef, useState } from "react";
import { Form, Input, Button, Col, Row, Space, Switch, Tooltip } from "antd";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { gameUseMutation } from "../../hooks";
import { Localize, EntityRemoveButton } from "components/service";
import { successNotification } from "components/request-result";
import notifyMutationError from "components/request-result/notify-mutation-error";

const normalizeGameValues = (values) => ({
  title: (values.title ?? "").trim(),
  link_site: (values.link_site ?? "").trim(),
  active: values.active ?? null,
});

const hasGameChanges = (values, baseline) => {
  const normalized = normalizeGameValues(values);
  return normalized.title !== baseline.title || normalized.link_site !== baseline.link_site || normalized.active !== baseline.active;
};

const GameEditForm = ({
  game = {}, onSaved, onDeleted, onCancel, autoFocus = false, visible = true, disabled = false,
  onBusyChange = () => {}, prepareCreate = async () => ({}), dragHandle,
}) => {
  const [form] = Form.useForm();
  const formId = useId();
  const busy = useRef(false);
  const initialValues = useRef({ title: game.title ?? "", link_site: game.link_site ?? "", active: game.id ? game.active ?? null : true });
  const baseline = useRef(normalizeGameValues(initialValues.current));
  const [isDirty, setIsDirty] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const active = Form.useWatch("active", form);
  const invalidResponseMessage = Localize({ children: "APP_SETTINGS.Error_InvalidResponse" }).props.children;
  const invalidUrlMessage = Localize({ children: "APP_SETTINGS.Invalid_GameUrl" }).props.children;
  const entityName = Localize({ children: "APP_SETTINGS.Entity_Game" }).props.children;
  const deleteLabel = Localize({ children: "GLOBAL.Button_Text_Delete" }).props.children;
  const titleLabel = Localize({ children: "APP_SETTINGS.Label_Title" }).props.children;
  const urlLabel = Localize({ children: "APP_SETTINGS.Label_GameUrl" }).props.children;
  const activeLabel = Localize({ children: "APP_SETTINGS.Label_Active" }).props.children;
  const titlePlaceholder = Localize({ children: "APP_SETTINGS.Placeholder_GameTitle" }).props.children;
  const urlPlaceholder = Localize({ children: "APP_SETTINGS.Placeholder_GameUrl" }).props.children;
  const id = game.id;
  const { _setGameCreateUpdate, loadingMutationGameCreateUpdate } = gameUseMutation.createUpdate(id);
  const { _setMutationGameDelete, loadingMutationGameDelete } = gameUseMutation.delete(id);
  const loading = isBusy || loadingMutationGameCreateUpdate || loadingMutationGameDelete;
  const locked = disabled || loading;

  const save = async (values) => {
    if (disabled || busy.current || !hasGameChanges(values, baseline.current)) return;
    busy.current = true;
    setIsBusy(true);
    onBusyChange(true);

    try {
      const normalized = normalizeGameValues(values);
      const prepared = id ? {} : await prepareCreate();
      const { data } = await _setGameCreateUpdate({
        variables: {
          input: {
            ...(id ? { id } : {}),
            title: normalized.title,
            link_site: normalized.link_site,
            ...(!id || normalized.active !== baseline.current.active ? { active: normalized.active } : {}),
            ...(!id && prepared?.row_order !== undefined ? { row_order: prepared.row_order } : {}),
          },
        },
      });
      const payload = data?.[id ? "gameUpdate" : "gameCreate"];
      if (!payload?.game?.id) throw new Error(invalidResponseMessage);

      const savedValues = { title: payload.game.title ?? "", link_site: payload.game.link_site ?? "", active: payload.game.active ?? null };
      baseline.current = normalizeGameValues(savedValues);
      form.setFieldsValue(savedValues);
      setIsDirty(false);
      await onSaved?.(payload.game);
      successNotification({ title: payload.label, description: payload.message });
    } catch (error) {
      notifyMutationError(error);
    } finally {
      busy.current = false;
      setIsBusy(false);
      onBusyChange(false);
    }
  };

  const remove = async (options) => {
    if (disabled || busy.current) return false;
    busy.current = true;
    setIsBusy(true);
    onBusyChange(true);

    try {
      const { data } = await _setMutationGameDelete(options);
      if (!data?.gameDelete) throw new Error(invalidResponseMessage);

      await onDeleted?.(id);
      successNotification({ title: data.gameDelete.label, description: data.gameDelete.message });
      return true;
    } catch (error) {
      // Resolve after reporting so EntityRemoveButton does not report the same error again.
      notifyMutationError(error);
      return false;
    } finally {
      busy.current = false;
      setIsBusy(false);
      onBusyChange(false);
    }
  };

  return (
    <Form
      form={form}
      name={`game-${formId}`}
      layout="vertical"
      className="model-form app-settings-row game-edit-form"
      disabled={locked}
      initialValues={initialValues.current}
      onValuesChange={(_, values) => setIsDirty(hasGameChanges(values, baseline.current))}
      onFinish={save}
    >
      <Row gutter={12} align="middle" className="app-settings-game-row">
        <Col className="app-settings-game-drag">
          {dragHandle ?? <Tooltip title={<Localize>APP_SETTINGS.Sorting_Unavailable</Localize>} open={visible ? undefined : false}>
            <span className="app-settings-drag-handle" aria-disabled="true">
              <HolderOutlined aria-hidden="true" />
            </span>
          </Tooltip>}
        </Col>
        <Col className="app-settings-game-title">
          <Form.Item
            name="title"
            label={<Localize>APP_SETTINGS.Label_Title</Localize>}
            rules={[{
              required: true,
              whitespace: true,
              message: <Localize>APP_SETTINGS.Required_Title</Localize>,
            }]}
          >
            <Input name="title" autoFocus={autoFocus && visible} aria-label={titleLabel} placeholder={titlePlaceholder} />
          </Form.Item>
        </Col>
        <Col className="app-settings-game-url">
          <Form.Item
            name="link_site"
            label={<Localize>APP_SETTINGS.Label_GameUrl</Localize>}
            rules={[
              {
                required: true,
                whitespace: true,
                message: <Localize>APP_SETTINGS.Required_GameUrl</Localize>,
              },
              {
                validator(_, value) {
                  if (!value?.trim()) return Promise.resolve();

                  try {
                    const url = new URL(value.trim());
                    if (/^https?:\/\//i.test(value.trim()) && url.hostname && ["http:", "https:"].includes(url.protocol)) {
                      return Promise.resolve();
                    }
                  } catch (error) {
                    // Invalid or relative URLs are handled by the field validation message.
                  }
                  return Promise.reject(new Error(invalidUrlMessage));
                },
              },
            ]}
          >
            <Input name="link_site" aria-label={urlLabel} placeholder={urlPlaceholder} />
          </Form.Item>
        </Col>
        <Col className="app-settings-game-active">
          <Form.Item
            name="active"
            label={<Localize>APP_SETTINGS.Label_Active</Localize>}
            valuePropName="checked"
            getValueProps={(value) => ({ checked: value === true })}
            extra={active === null ? <Localize>APP_SETTINGS.Active_NotSet</Localize> : undefined}
          >
            <Switch aria-label={activeLabel} />
          </Form.Item>
        </Col>
        <Col className="app-settings-game-actions">
          <Space>
            <Button type="primary" size="middle" htmlType="submit" loading={isBusy && !loadingMutationGameDelete} disabled={locked || !isDirty}>
              <Localize>APP_SETTINGS.Save</Localize>
            </Button>
            {id ? (
              <EntityRemoveButton
                modalButton={<Button type="text" size="middle" danger icon={<DeleteOutlined />} disabled={locked} aria-label={deleteLabel} />}
                tooltip={<Localize>GLOBAL.Button_Text_Delete</Localize>}
                confirmation="popover"
                visible={visible}
                nameEntity={entityName}
                dataNameEntity={game.title}
                loading={loadingMutationGameDelete}
                disabled={locked}
                deleteMutation={remove}
                variables={{ id }}
              />
            ) : (
              <Button onClick={onCancel} disabled={locked}>
                <Localize>GLOBAL.Button_Text_Cancel</Localize>
              </Button>
            )}
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default GameEditForm;
